import { AxiosResponse } from 'axios';
import { format, parse, startOfToday, startOfYesterday } from 'date-fns';
import {
  all,
  call,
  cancelled,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { DataKiParams } from '../domain/dataKi';
import {
  DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
  DATA_KI_GRAPH_MAX_SLOTS,
  DataKiGraph,
  DataKiGraphCollection,
  DataKiGraphFieldItem,
  DataKiGraphParams,
  DataKiGraphSlotConfig,
  GRAPH_DATE_TYPE,
  GRAPH_FIELD_TYPE,
  GraphDateType,
} from '../domain/dataKiGraph';
import {
  graphSlotKey,
  normalizeGraphSlotsForFetch,
  resolveChangedGraphSlotsForRefetch,
  resolveDataKiGraphSlots,
  resolveDataKiGraphSlotsForSearch,
  resolveDkSisConditionFromSearchParams,
} from '../domain/dataKiGraphSlots';
import { KiTagListOptions } from '../domain/schemas';

import {
  dataKiFilteredKiTagAverageSelector,
  dataKiParamsSelector,
} from '../redux/server/dataKi';
import { DataKiDaiActionCreators } from '../redux/server/dataKiDai';
import {
  ChangeDataKiGraphDkSisSearchParamsAction,
  ChangeDateTypeAction,
  ChangeFieldAction,
  DataKiGraphActionCreators,
  DataKiGraphActionTypes,
  FetchDataKiGraphAction,
  FetchDataKiGraphFromFavoriteAction,
  SetDataKiGraphSlotsAction,
  dataKiGraphDateTypeSelector,
  dataKiGraphParamsSelector,
  dataKiGraphRawSelector,
} from '../redux/server/dataKiGraph';
import { DataKiKasidamaActionCreators } from '../redux/server/dataKiKasidama';
import { DataKiMksTransitiveGraphActionCreators } from '../redux/server/dataKiMksTransitiveGraph';
import { settingsOptionsKiSearchConditionSelector } from '../redux/server/settingsOptionsKi';
import { ModelReportsSettingActionCreators } from '../redux/ui/modelReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { dataKiGraphSearchConditionParams } from '../utils/dataKiGraphSearchConditionParams';
import { calcStartDateFromDateType } from '../utils/date';
import {
  NO_GRAPH,
  convertOptionValueToSisParam,
} from '../utils/graphConditionSelectorUtils';
import { handleErrorSaga } from './errorSaga';

type GraphApiResponse = DataKiGraph;

type GraphSlotFetchSuccess = {
  slot: DataKiGraphSlotConfig;
  response: GraphApiResponse;
};

type GraphSlotFetchFailure = {
  slot: DataKiGraphSlotConfig;
  error: unknown;
};

type GraphSlotFetchResult = GraphSlotFetchSuccess | GraphSlotFetchFailure;

const isGraphSlotFetchSuccess = (
  result: GraphSlotFetchResult
): result is GraphSlotFetchSuccess => 'response' in result;

const isGraphSlotFetchFailure = (
  result: GraphSlotFetchResult
): result is GraphSlotFetchFailure => 'error' in result;

/**
 * 推移グラフ（店舗比較）表示ボタンをクリックされた行の情報を元に推移グラフ（店舗比較）の検索条件を加工する
 * @param queryParams クリックされたメインフィールドの行（店舗行じゃない方）
 * @param searchParams 機種集計の検索条件
 * @returns 加工された検索条件
 */
export const queryParameterToGraphParams = (
  searchParams: DataKiParams
): DataKiGraphParams => {
  const params: DataKiGraphParams = {
    field: GRAPH_FIELD_TYPE.OUT,
    seriesType: 'hall',
    containsAverage: true,
    dateType: GRAPH_DATE_TYPE.DAILY,
    // 現在の検索条件を混ぜ込む
    ...{
      hallsForDonyuJokyo: searchParams.hallsForDonyuJokyo,
      halls: searchParams.halls,
      areas: searchParams.areas,
      startDate: searchParams.ymdList && searchParams.ymdList[0],
      endDate: searchParams.ymdList && searchParams.ymdList.slice(-1)[0],
      shuList: searchParams.shuList,
      shuGroupIds: searchParams.shuGroupIds,
    },
  };

  // 現在の検索条件を混ぜ込む
  if (searchParams.isDonyuForDonyuJokyo !== undefined) {
    params.isDonyuForDonyuJokyo = searchParams.isDonyuForDonyuJokyo;
  }
  if (searchParams.excludeToday !== undefined) {
    params.excludeToday = searchParams.excludeToday;
  }

  return params;
};

// 複数スロット化で field / graphSlots / sisXxx / needsSis は廃止。
// 表示項目と DK-SIS 条件はスロット単位（slot.field / slot.dkSisCondition）で送り直すため、
// baseParams からはこれらを除外してからスロット由来の値を上書きする。
const toGraphApiQueryParams = (
  baseParams: DataKiGraphParams,
  slot: DataKiGraphSlotConfig
) => {
  const {
    field: _field,
    graphSlots: _graphSlots,
    sisBreakEvenForPachinko: _sisBreakEvenForPachinko,
    sisBreakEvenForPachislo: _sisBreakEvenForPachislo,
    sisRank: _sisRank,
    sisArea: _sisArea,
    needsSis: _needsSis,
    ...apiParams
  } = baseParams;

  return {
    ...apiParams,
    field: slot.field,
    ...convertOptionValueToSisParam(slot.dkSisCondition),
  };
};

const buildDataKiGraphCollection = (
  responses: GraphApiResponse[],
  slots: DataKiGraphSlotConfig[],
  // setting.graphSlots に残す設定一覧。既定は slots だが、部分失敗時は
  // 全要求スロット（失敗分含む）を渡し、設定・お気に入りから欠落させない。
  configSlots: DataKiGraphSlotConfig[] = slots
): DataKiGraphCollection => {
  const firstSetting = responses.at(0)?.setting;
  if (!firstSetting) {
    throw new Error('Graph API response is empty');
  }

  const graphList: DataKiGraphFieldItem[] = slots.flatMap((slot, index) => {
    const response = responses.at(index);
    if (!response || slot.field === '') {
      return [];
    }
    return [
      {
        field: slot.field,
        dkSisCondition: slot.dkSisCondition,
        data: response.data,
      },
    ];
  });

  return {
    setting: {
      ...firstSetting,
      graphSlots: configSlots,
    },
    graphList,
  };
};

const buildMergedDataKiGraphCollection = (
  existingGraph: DataKiGraphCollection | undefined,
  responses: GraphApiResponse[],
  fetchedSlots: DataKiGraphSlotConfig[],
  targetSlots: DataKiGraphSlotConfig[]
): DataKiGraphCollection => {
  const firstSetting = responses.at(0)?.setting ?? existingGraph?.setting;
  if (!firstSetting) {
    throw new Error('Graph API response is empty');
  }

  const graphByKey = new Map(
    (existingGraph?.graphList ?? []).map((item) => [
      graphSlotKey({
        field: item.field,
        dkSisCondition: item.dkSisCondition,
      }),
      item,
    ])
  );

  fetchedSlots.forEach((slot, index) => {
    const response = responses.at(index);
    if (!response || slot.field === '') {
      return;
    }
    graphByKey.set(graphSlotKey(slot), {
      field: slot.field,
      dkSisCondition: slot.dkSisCondition,
      data: response.data,
    });
  });

  const graphList = normalizeGraphSlotsForFetch(targetSlots).flatMap((slot) => {
    const item =
      graphByKey.get(graphSlotKey(slot)) ??
      // キー完全一致がなければ同一表示項目の既存データで暫定表示する
      // （DK-SIS 条件変更の取得待ち中に該当スロットを空白にしないためのフォールバック）
      [...graphByKey.values()].find(
        (candidate) => candidate.field === slot.field
      );
    return item ? [item] : [];
  });

  return {
    setting: {
      ...firstSetting,
      graphSlots: targetSlots,
    },
    graphList,
  };
};

function* fetchGraphBySlot(
  api: Api,
  baseParams: DataKiGraphParams,
  slot: DataKiGraphSlotConfig
) {
  const response: AxiosResponse<GraphApiResponse> = yield call(
    api.get,
    '/data/ki/graph',
    buildConfig(toGraphApiQueryParams(baseParams, slot))
  );
  return response.data;
}

function* fetchGraphBySlotSafely(
  api: Api,
  baseParams: DataKiGraphParams,
  slot: DataKiGraphSlotConfig
): Generator<unknown, GraphSlotFetchResult, GraphApiResponse> {
  try {
    const response = yield call(fetchGraphBySlot, api, baseParams, slot);
    return { slot, response };
  } catch (error: unknown) {
    return { slot, error };
  }
}

function* fetchDataKiGraphBySlotsSaga(
  api: Api,
  baseParams: DataKiGraphParams,
  graphSlots: DataKiGraphSlotConfig[]
) {
  const slots = normalizeGraphSlotsForFetch(
    graphSlots,
    DATA_KI_GRAPH_MAX_SLOTS
  );
  if (slots.length === 0) {
    return;
  }

  yield put(DataKiGraphActionCreators.showLoadingAction());

  // takeLatest キャンセルで途中終了しても loading を残さないよう finally で解除する
  try {
    const results: GraphSlotFetchResult[] = yield all(
      slots.map((slot) => call(fetchGraphBySlotSafely, api, baseParams, slot))
    );

    const successfulResults = results.filter(isGraphSlotFetchSuccess);
    const failedResults = results.filter(isGraphSlotFetchFailure);

    if (successfulResults.length === 0) {
      // 初回・全スロット取得の全失敗時は既存グラフを破棄する（renew でクリア）
      const firstError = failedResults.at(0)?.error;
      yield put(DataKiGraphActionCreators.renewDataKiGraphAction());
      if (firstError !== undefined) {
        yield fork(handleErrorSaga, firstError);
      }
      return;
    }

    const graph = buildDataKiGraphCollection(
      successfulResults.map((result) => result.response),
      successfulResults.map((result) => result.slot),
      // 失敗スロットも設定として保持（graphList は成功分のみ）。
      // 期間変更などの次回全取得で再取得され、お気に入り保存でも欠落しない。
      slots
    );

    yield put(DataKiGraphActionCreators.fetchDataKiGraphSuccessAction(graph));
    yield put(DataKiGraphActionCreators.hideLoadingAction());

    if (failedResults.length > 0) {
      console.warn(
        `[dataKiGraphSagas] 推移グラフの一部スロットの取得に失敗しました: ${failedResults
          .map((result) => `${result.slot.field}:${result.slot.dkSisCondition}`)
          .join(', ')}`
      );
    }
  } finally {
    const isCancelled: boolean = yield cancelled();
    if (isCancelled) {
      yield put(DataKiGraphActionCreators.hideLoadingAction());
    }
  }
}

function* fetchAndMergeDataKiGraphBySlotsSaga(
  api: Api,
  baseParams: DataKiGraphParams,
  slotsToFetch: DataKiGraphSlotConfig[],
  targetSlots: DataKiGraphSlotConfig[]
) {
  if (slotsToFetch.length === 0) {
    return;
  }

  const existingGraph: DataKiGraphCollection | undefined = yield select(
    dataKiGraphRawSelector
  );

  yield put(DataKiGraphActionCreators.showLoadingAction());

  // takeLatest キャンセルで途中終了しても loading を残さないよう finally で解除する
  try {
    const results: GraphSlotFetchResult[] = yield all(
      slotsToFetch.map((slot) =>
        call(fetchGraphBySlotSafely, api, baseParams, slot)
      )
    );

    const successfulResults = results.filter(isGraphSlotFetchSuccess);
    const failedResults = results.filter(isGraphSlotFetchFailure);

    if (successfulResults.length === 0) {
      // 差分再取得の全失敗時は既存グラフを消さず据え置く（ローディング解除のみ）。
      // 全取得（getDataKiGraphSaga）の全失敗が renew で破棄するのと対照的。
      const firstError = failedResults.at(0)?.error;
      yield put(DataKiGraphActionCreators.hideLoadingAction());
      if (firstError !== undefined) {
        yield fork(handleErrorSaga, firstError);
      }
      return;
    }

    const graph = buildMergedDataKiGraphCollection(
      existingGraph,
      successfulResults.map((result) => result.response),
      successfulResults.map((result) => result.slot),
      targetSlots
    );

    yield put(DataKiGraphActionCreators.fetchDataKiGraphSuccessAction(graph));
    yield put(DataKiGraphActionCreators.hideLoadingAction());

    if (failedResults.length > 0) {
      console.warn(
        `[dataKiGraphSagas] 推移グラフの一部スロットの取得に失敗しました: ${failedResults
          .map((result) => `${result.slot.field}:${result.slot.dkSisCondition}`)
          .join(', ')}`
      );
    }
  } finally {
    const isCancelled: boolean = yield cancelled();
    if (isCancelled) {
      yield put(DataKiGraphActionCreators.hideLoadingAction());
    }
  }
}

/**
 * パラメータを元に推移グラフ（店舗比較）データを取得する
 * @param api AxiosInstace
 * @param params 検索条件（DataKiGraphParams）
 */
export function* getDataKiGraphSaga(
  api: Api,
  params: DataKiGraphParams,
  graphSlots?: DataKiGraphSlotConfig[]
) {
  try {
    // 現在の検索条件を取得
    const searchParams: DataKiParams = yield select(dataKiParamsSelector);

    // 現在の期間（最終日）を取得する
    const searchEndDate =
      params.endDate == null
        ? startOfToday()
        : parse(params.endDate, 'yyyy-MM-dd', new Date());
    // 当日除外かつ今日を含んでいる場合は今日より前の日付から算出する
    const endDate =
      searchParams.excludeToday && searchEndDate >= startOfToday()
        ? startOfYesterday()
        : searchEndDate;

    // 開始期間を算出
    const startDate = calcStartDateFromDateType(params.dateType, endDate);

    // 他のデータがあれば、同時に表示することがないためクリアする
    yield put(DataKiDaiActionCreators.hideDataKiDai());
    yield put(DataKiKasidamaActionCreators.hideDataKiKasidama());
    yield put(
      DataKiMksTransitiveGraphActionCreators.hideDataKiMksTransitiveGraphAction()
    );

    yield call(
      fetchDataKiGraphBySlotsSaga,
      api,
      {
        ...params,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      },
      graphSlots ?? resolveDataKiGraphSlots(params)
    );
  } catch (error: unknown) {
    yield put(DataKiGraphActionCreators.renewDataKiGraphAction());
    yield fork(handleErrorSaga, error);
  }
}

// 現在の検索条件を元に推移グラフ（店舗比較）データを取得する（初回取得）
export function* fetchDataKiGraphSaga(
  api: Api,
  action: FetchDataKiGraphAction
) {
  // NOTE:
  // グラフ取得前に「どの行を起点にグラフを開いたか」を Redux に保存する。
  // これは UI 側で平均/合計行判定を確定的に行うために利用する（件数比較だけでは誤判定が起きるため）。
  // - 通常のテーブルからグラフを開くルートではここで設定される。
  // - お気に入り経由の fetch ではこの dispatch は行われない設計になっている（フォールバックあり）。
  // 前回の閉じアニメーションが中断されフラグがfalseのまま残っていても開けるよう、取得開始時に有効化する
  yield put(
    ModelReportsSettingActionCreators.selectModelReportsDerivedScreenAnimationEnabledAction(
      'graph',
      true
    )
  );
  yield put(
    DataKiGraphActionCreators.changeDataKiGraphOpenedQueryParameterAction(
      action.payload.queryParams
    )
  );

  // 現在の検索条件を取得
  const searchParams: DataKiParams = yield select(dataKiParamsSelector);
  // 選択中の表示項目と表示期間を取得
  const dateType: GraphDateType = yield select(dataKiGraphDateTypeSelector);
  const onlyOneKiTagAverageTagIds = action.payload.onlyOneKiTagAverageTagIds; // 各機種タグ平均合計行から推移グラフを表示する際に利用するタグID
  // 選択された機種タグ一覧を取得するために、filteredKiTagAverageからvalue値を取得
  const tags: {
    value: KiTagListOptions[];
  } = yield select(dataKiFilteredKiTagAverageSelector);
  const settingsOptions = yield* customSelect(
    settingsOptionsKiSearchConditionSelector
  );

  const fetchedDataKiGraphSearchCondition = dataKiGraphSearchConditionParams(
    action.payload.queryParams,
    action.payload.requestParams,
    searchParams,
    onlyOneKiTagAverageTagIds,
    settingsOptions.makers
  );

  const existingGraphParams: DataKiGraphParams | undefined = yield select(
    dataKiGraphParamsSelector
  );
  const initialDkSisCondition = resolveDkSisConditionFromSearchParams(
    action.payload.dkSisSearchParams
  );

  // 機種タグ平均合計 / 各機種タグ平均合計 / タグ除外平均合計 の行では DK-SIS を送らない
  // https://github.com/DKClaris/claris-general/issues/3652
  // https://github.com/DKClaris/claris-general/issues/3734
  const isKiTagAverageGraphRow =
    action.payload.queryParams.value === 'kiTagAverage' ||
    action.payload.queryParams.value === 'excludedKiTagAverage' ||
    action.payload.queryParams.value ===
      `onlyOneKiTagAverage_${onlyOneKiTagAverageTagIds?.at(0)?.toString()}`;

  const resolvedGraphSlots = resolveDataKiGraphSlotsForSearch(
    existingGraphParams,
    initialDkSisCondition
  );
  // 実際に API へ渡るのは slot.dkSisCondition（toGraphApiQueryParams が baseParams の
  // needsSis を捨てるため）。タグ平均系の行は各スロット条件を noGraph に固定して抑止する。
  const graphSlots = isKiTagAverageGraphRow
    ? resolvedGraphSlots.map((slot) => ({
        ...slot,
        dkSisCondition: NO_GRAPH,
      }))
    : resolvedGraphSlots;

  // 推移グラフ（店舗比較）の取得条件を指定
  const graphParams = {
    ...queryParameterToGraphParams(searchParams),
    ...fetchedDataKiGraphSearchCondition,
    graphSlots,
  };
  graphParams.dateType = dateType;

  // 機種タグ平均合計行の推移グラフから選択された場合に、推移グラフ機種セレクタに「タグ絞り込み平均合計」を表示するための条件
  if (!action.payload.isSelectedFromMainField) {
    // 機種タグ平均合計行の推移グラフ機種セレクタに「タグ絞り込み平均合計」を選択した場合、推移グラフ機種セレクタに表示する平均合計行を正しく表示するための条件
    yield put(
      ModelReportsSettingActionCreators.selectModelReportsIsKiTagAverageGraphAction(
        action.payload.queryParams.value === 'kiTagAverage'
      )
    );
    yield put(
      ModelReportsSettingActionCreators.selectModelReportsIsExcludedKiTagAverageGraphAction(
        action.payload.queryParams.value === 'excludedKiTagAverage'
      )
    );
    // 各機種タグ平均合計行の推移グラフ機種セレクタに「タグ絞り込み平均合計」を選択した場合に、推移グラフ機種セレクタに表示する平均合計行を正しく表示するための条件
    yield put(
      ModelReportsSettingActionCreators.selectModelReportsIsOnlyOneKiTagAverageGraphAction(
        action.payload.queryParams.value ===
          `onlyOneKiTagAverage_${onlyOneKiTagAverageTagIds?.at(0)?.toString()}`
      )
    );
  }

  // 機種タグ平均合計行 / タグ除外平均合計行では機種タグ一覧を送る（DK-SIS 抑止は graphSlots 側で実施）
  if (action.payload.queryParams.value === 'kiTagAverage') {
    graphParams.tagIds = tags.value.map((tag) => tag.tagId.toString());
  }

  if (action.payload.queryParams.value === 'excludedKiTagAverage') {
    graphParams.tagIds = tags.value.map((tag) => tag.tagId.toString());
    graphParams.isExcludeForTagIds = true;
  }

  // 推移グラフ押下時や推移グラフから機種セレクタを選択した際に、どの機種タグ平均合計行の機種タグから選択されたかを判断するための設定
  if (onlyOneKiTagAverageTagIds) {
    graphParams.tagIds = onlyOneKiTagAverageTagIds.map((tag) => tag.toString());
  }

  // 選択中の店舗を登録
  yield put(
    DataKiGraphActionCreators.changeSelectHallsAction(
      action.payload.selectHalls
    )
  );

  // 選択中の機種行がどの「メーカーID・ホールID」を持つのかを判断するために登録
  if (action.payload.requestParams) {
    yield put(
      ModelReportsSettingActionCreators.changeSelectedDataKiExpandedRowAction(
        action.payload.requestParams.mainFieldId,
        action.payload.requestParams.hallId,
        '3rdRow'
      )
    );
  } else {
    yield put(
      ModelReportsSettingActionCreators.changeSelectedDataKiExpandedRowAction(
        '',
        '',
        '1stRow'
      )
    );
  }

  // 推移グラフ（店舗比較）を取得
  yield call(getDataKiGraphSaga, api, graphParams, graphSlots);
}

// グラフ用の検索条件を指定して推移グラフ（店舗比較）データを取得する（お気に入り適用）
function* fetchDataKiGraphForFavoriteSaga(
  api: Api,
  action: FetchDataKiGraphFromFavoriteAction
) {
  // 前回の閉じアニメーションが中断されフラグがfalseのまま残っていても開けるよう、取得開始時に有効化する
  yield put(
    ModelReportsSettingActionCreators.selectModelReportsDerivedScreenAnimationEnabledAction(
      'graph',
      true
    )
  );
  // 推移グラフ（店舗比較）を取得
  const graphSlots = resolveDataKiGraphSlots(action.payload.params);
  yield call(getDataKiGraphSaga, api, action.payload.params, graphSlots);
}

// 指定されたグラフ項目でグラフを再取得
function* changeFieldSaga(api: Api, action: ChangeFieldAction) {
  const searchCondition: DataKiGraphParams | undefined = yield select(
    dataKiGraphParamsSelector
  ); // 現在の検索条件
  if (!searchCondition) {
    return;
  }

  const [firstSlot, ...restSlots] = resolveDataKiGraphSlots(searchCondition);
  const graphSlots = [
    {
      ...(firstSlot ?? {
        field: GRAPH_FIELD_TYPE.OUT,
        dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
      }),
      field: action.payload.field,
    },
    ...restSlots,
  ];

  // グラフデータを取得し直す
  yield call(
    getDataKiGraphSaga,
    api,
    {
      ...searchCondition,
      field: action.payload.field,
      graphSlots,
    },
    graphSlots
  );
}

// 指定された期間でグラフを再取得
function* changeDateTypeSaga(api: Api, action: ChangeDateTypeAction) {
  const searchCondition: DataKiGraphParams | undefined = yield select(
    dataKiGraphParamsSelector
  ); // 現在の検索条件
  if (!searchCondition) {
    return;
  }
  const graphSlots = resolveDataKiGraphSlots(searchCondition);

  // グラフデータを取得し直す
  yield call(
    getDataKiGraphSaga,
    api,
    {
      ...searchCondition,
      dateType: action.payload.dateType,
      graphSlots,
    },
    graphSlots
  );
}

// 指定されたDK-SIS条件でグラフを再取得
function* changeDkSisSearchParamsSaga(
  api: Api,
  action: ChangeDataKiGraphDkSisSearchParamsAction
) {
  const searchGraphParams: DataKiGraphParams | undefined = yield select(
    dataKiGraphParamsSelector
  ); // 現在の検索条件
  // https://app.zenhub.com/workspaces/claris-60b56db504e3ff00150ebc22/issues/gh/dkclaris/claris-general/2421
  // 推移グラフのDK-SIS条件を変更するときには、グラフデータが存在していることを前提としているので、データがない場合には変更処理を実行しない
  // ※主に、お気に入り適用時にバグを発生させないための処置
  if (!searchGraphParams) {
    return;
  }

  const [firstSlot, ...restSlots] = resolveDataKiGraphSlots(searchGraphParams);
  const graphSlots = [
    {
      ...(firstSlot ?? {
        field: GRAPH_FIELD_TYPE.OUT,
        dkSisCondition: DATA_KI_GRAPH_DEFAULT_DK_SIS_CONDITION,
      }),
      dkSisCondition: resolveDkSisConditionFromSearchParams(
        action.payload.params
      ),
    },
    ...restSlots,
  ];

  // グラフデータを取得し直す
  yield call(
    getDataKiGraphSaga,
    api,
    {
      ...searchGraphParams,
      graphSlots,
    },
    graphSlots
  );
}

function* setDataKiGraphSlotsSaga(api: Api, action: SetDataKiGraphSlotsAction) {
  const searchCondition: DataKiGraphParams | undefined = yield select(
    dataKiGraphParamsSelector
  );
  if (!searchCondition) {
    console.warn(
      '[dataKiGraphSagas] グラフ検索条件が未設定のため再取得をスキップしました'
    );
    return;
  }

  const existingGraph: DataKiGraphCollection | undefined = yield select(
    dataKiGraphRawSelector
  );
  const prevSlots =
    existingGraph?.graphList.map((item) => ({
      field: item.field,
      dkSisCondition: item.dkSisCondition,
    })) ?? resolveDataKiGraphSlots(searchCondition);
  const nextSlots = normalizeGraphSlotsForFetch(
    action.payload.graphSlots,
    DATA_KI_GRAPH_MAX_SLOTS
  );

  // 既存データがある場合は、変わったスロットだけ取得して残りは使い回す。
  // このサガはスロット構成の変更（項目変更・DK-SIS 条件変更・追加）だけを扱い、
  // 期間・検索条件の変更は別サガ（getDataKiGraphSaga）を通るため、常に差分取得で足りる。
  // 全再取得だと未変更スロットのデータ参照まで作り直され、無駄なリクエストと再レンダーが発生する。
  if (existingGraph) {
    const slotsToFetch = resolveChangedGraphSlotsForRefetch(
      prevSlots,
      nextSlots
    );
    if (slotsToFetch.length > 0) {
      yield call(
        fetchAndMergeDataKiGraphBySlotsSaga,
        api,
        {
          ...searchCondition,
          graphSlots: nextSlots,
        },
        slotsToFetch,
        nextSlots
      );
      return;
    }
    // 取得対象なし（構成のみ変化）→ 既存データのまま graphList を組み直す
    yield put(
      DataKiGraphActionCreators.setDataKiGraphGraphListAction(nextSlots)
    );
    return;
  }

  // 既存グラフが無い（初回取得など）→ 従来どおり全スロットを取得する
  yield call(
    getDataKiGraphSaga,
    api,
    {
      ...searchCondition,
      graphSlots: nextSlots,
    },
    nextSlots
  );
}

// CHANGE_DATA_KI_GRAPH_FIELDがdispatchされたタイミングで推移グラフ（店舗比較）を再取得
function* handleChangeFieldSaga(api: Api) {
  yield takeEvery(
    DataKiGraphActionTypes.CHANGE_DATA_KI_GRAPH_FIELD,
    changeFieldSaga,
    api
  );
}

// CHANGE_DATA_KI_GRAPH_DATE_TYPEがdispatchされたタイミングで推移グラフ（店舗比較）を再取得
function* handleChangeDateTypeSaga(api: Api) {
  yield takeEvery(
    DataKiGraphActionTypes.CHANGE_DATA_KI_GRAPH_DATE_TYPE,
    changeDateTypeSaga,
    api
  );
}

// FETCH_DATA_KI_GRAPHがdispatchされたタイミングで推移グラフ（店舗比較）を取得
function* handleFetchDataKiGraphSaga(api: Api) {
  yield takeEvery(
    DataKiGraphActionTypes.FETCH_DATA_KI_GRAPH,
    fetchDataKiGraphSaga,
    api
  );
}

// FETCH_DATA_KI_GRAPHがdispatchされたタイミングで推移グラフ（店舗比較）を取得
function* handleFetchDataKiGraphForFavoriteSaga(api: Api) {
  yield takeEvery(
    DataKiGraphActionTypes.FETCH_DATA_KI_GRAPH_FROM_FAVORITE,
    fetchDataKiGraphForFavoriteSaga,
    api
  );
}

// CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMSがdispatchされたタイミングで推移グラフ（店舗比較）を再取得
function* handleChangeDkSisSearchParamsSaga(api: Api) {
  yield takeEvery(
    DataKiGraphActionTypes.CHANGE_DATA_KI_GRAPH_DK_SIS_SEARCH_PARAMS,
    changeDkSisSearchParamsSaga,
    api
  );
}

function* handleSetDataKiGraphSlotsSaga(api: Api) {
  yield takeLatest(
    DataKiGraphActionTypes.SET_DATA_KI_GRAPH_SLOTS,
    setDataKiGraphSlotsSaga,
    api
  );
}

// 推移グラフ（店舗比較）に関するタスクを実行
export function* dataKiGraphSagas(context: { api: Api }) {
  yield fork(handleFetchDataKiGraphSaga, context.api);
  yield fork(handleFetchDataKiGraphForFavoriteSaga, context.api);
  yield fork(handleChangeFieldSaga, context.api);
  yield fork(handleChangeDateTypeSaga, context.api);
  yield fork(handleChangeDkSisSearchParamsSaga, context.api);
  yield fork(handleSetDataKiGraphSlotsSaga, context.api);
}
